import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from '@dr.pogodin/react-helmet'
import useAuth from '../../../hooks/useAuth'
import {
    Card as MuiCard,
    CardContent,
    Divider,
    Typography,
    Grid,
    Tab,
    LinearProgress,
    Button,
    CircularProgress,
    Stack,
} from '@mui/material'
import { RegisteredTextField } from '../../../components/fields/TextField'
import styled from '@emotion/styled'
import { spacing } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { TabPanel, TabContext, TabList } from '@mui/lab'
import { useForm, FormProvider } from 'react-hook-form'
import { FactsTable, FactsTableRow } from '../../components/FactsTable'
import CompanyApi from '../../../api/CompanyApi'
import ShareApi from '../../../api/ShareApi'

const Card = styled(MuiCard)(spacing)

const BasicFields = ({ company, submitLabel, handleSaveCompany }) => {
    const [saving, setSaving] = useState(false)
    const handleOnClick = async () => {
        setSaving(true)
        await handleSaveCompany()
        setSaving(false)
    }
    return (
        <FactsTable>
            <FactsTableRow label='Namn'>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 10 }}>
                        <Stack spacing={2}>
                            <Typography variant='body1' gutterBottom display='block'>
                                I appen: "{company.name}"
                            </Typography>
                            {company.millistreamName && (
                                <Typography variant='body1' gutterBottom display='block'>
                                    Från Millistream: "{company.millistreamName}"
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button variant='contained' onClick={handleOnClick}>
                            {submitLabel || 'Spara'} {saving && <CircularProgress size={24} />}
                        </Button>
                    </Grid>
                </Grid>
            </FactsTableRow>
            <FactsTableRow label='Registeringsnummer'>
                <RegisteredTextField label='Registeringsnummer' name='registrationNumber' fullWidth />
            </FactsTableRow>
            <FactsTableRow label='Landskod'>
                <RegisteredTextField label='Landskod' name='countryCode' fullWidth />
            </FactsTableRow>
        </FactsTable>
    )
}

function ShareFields({ share }) {
    return (
        <FactsTable>
            <FactsTableRow label='Namn' value={share.name} />
            <FactsTableRow label='ISIN' value={share.isin} />
            <FactsTableRow label='Marknadsplats' value={share.marketplace} />
            <FactsTableRow label='Symbol' value={share.symbol} />
            <FactsTableRow label='Valuta' value={share.currency} />
            <FactsTableRow label='Listad' value={share.listed ? 'Ja' : 'Nej'} />
        </FactsTable>
    )
}

function SharesTabs({ shares }) {
    const [selectedTab, setSelectedTab] = useState(shares ? shares[0].id : null)
    const shareTab = (share) => <Tab key={share.id} label={share.name} value={share.id} />
    const shareTabPanel = (share) => (
        <TabPanel key={share.id} value={share.id}>
            <ShareFields share={share} />
        </TabPanel>
    )
    return (
        <TabContext value={selectedTab}>
            <TabList onChange={(e, v) => setSelectedTab(v)}>{shares.map(shareTab)}</TabList>
            {shares.map(shareTabPanel)}
        </TabContext>
    )
}

const shareApi = new ShareApi()

function SharesDetails({ company }) {
    const [shares, setShares] = useState(null)
    const { isAuthenticated } = useAuth()

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !company) return
            const shares = await shareApi.findByCompanyId(company.id)
            // sort by name
            shares.sort((a, b) => a.name.localeCompare(b.name))
            setShares(shares)
        }
        fetchData()
    }, [isAuthenticated, company])
    const fetchedDataDone = company && shares
    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Typography
                    variant='h6'
                    gutterBottom
                    display='block'
                    style={{ marginTop: '1rem', marginBottom: '1rem' }}
                >
                    Aktier för {company?.name}
                </Typography>
            </Grid>
            <Grid size={12}>
                {!fetchedDataDone && <LinearProgress />}
                {fetchedDataDone && shares.length > 1 && <SharesTabs shares={shares} />}
                {fetchedDataDone && shares.length === 1 && <ShareFields share={shares[0]} />}
                {fetchedDataDone && shares.length === 0 && (
                    <Typography variant='body1'>Inga aktier hittades</Typography>
                )}
            </Grid>
        </Grid>
    )
}

const companyApi = new CompanyApi()

function CompanyEditForm({ company }) {
    // const formMethods = useForm({ defaultValues: company })
    const formMethods = useForm({
        defaultValues: { registrationNumber: company.registrationNumber, countryCode: company.countryCode },
    })
    const handleSaveCompany = async () => {
        const formData = formMethods.getValues()
        const { registrationNumber, countryCode } = formData
        const data = { registrationNumber, countryCode } // for now, these are the only fields we can update
        console.log({ data })
        await companyApi.updateCompany(company.id, data)
    }
    return (
        <FormProvider {...formMethods}>
            <Card mb={2}>
                <CardContent>
                    {company && <BasicFields company={company} handleSaveCompany={handleSaveCompany} />}
                </CardContent>
            </Card>
            <Card mb={6}>
                <CardContent>{company && <SharesDetails company={company} />}</CardContent>
            </Card>
        </FormProvider>
    )
}

function CompanyForm() {
    const { t } = useTranslation()
    const { isAuthenticated } = useAuth()
    const { companyId } = useParams()
    const [company, setCompany] = useState(null)
    const [companyName, setCompanyName] = useState(companyId === 'new' ? 'Nytt bolag' : '')
    const companyApi = new CompanyApi()

    useEffect(() => {
        if (!isAuthenticated || companyId === 'new') return
        async function fetchData() {
            const company = await companyApi.findByCompanyId(companyId)
            if (company) {
                setCompany(company)
                setCompanyName(company.name)
            }
        }
        fetchData()
    }, [isAuthenticated, companyId])

    return (
        <React.Fragment>
            <Helmet title={t('site.companies.detailsTitle', { name: companyName })} />
            <Typography variant='h3' gutterBottom display='block'>
                {t('site.companies.detailsTitle', { name: companyName })}
            </Typography>

            <Typography variant='body1' gutterBottom display='block'>
                Bolagsinformation i databasen, och aktier som är kopplade till bolaget.
            </Typography>

            <Divider my={6} />
            {companyId !== 'new' && !company && <LinearProgress />}
            {companyId !== 'new' && company && <CompanyEditForm company={company} />}
            {companyId === 'new' && (
                <Typography variant='h4' gutterBottom display='block'>
                    Vi stödjer inte längre att skapa nytt bolag från appen.
                </Typography>
            )}
        </React.Fragment>
    )
}

export default CompanyForm
